<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { SquareArrowRightUpIcon } from '@solar-icons/vue/linear'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppPageSection from '../../shell/AppPage/AppPageSection.vue'
import PostArtwork from '../PostArtwork/PostArtwork.vue'
import DesignPicker from '../DesignPicker/DesignPicker.vue'
import DesignRunStatus from '../../design/DesignRunStatus/DesignRunStatus.vue'
import { postMediaState } from '../../../lib/content/post-media'
import type { ContentItem } from '../../../types/content'
import type { DesignRun } from '../../../types/design'

const props = withDefaults(
  defineProps<{
    /** The post whose artwork the section shows, or offers to attach. */
    item: ContentItem
    headingLevel?: 'h2' | 'h3'
    /** The newest run designing this post's media, or `null` when none ran. */
    designRun?: DesignRun | null
  }>(),
  { headingLevel: 'h2', designRun: null },
)

const emit = defineEmits<{
  openDesign: [designId: string]
}>()

const { t } = useI18n()

const media = computed(() => postMediaState(props.item, props.designRun))
const attachDialogOpen = ref(false)
</script>

<template>
  <AppPageSection :title="t('content.post.mediaLabel')" :heading-level="headingLevel">
    <template v-if="item.design" #meta>{{
      t('calendar.pages', { count: item.design.pageCount }, item.design.pageCount)
    }}</template>
    <template v-if="media.openDesignId" #actions>
      <AppButton
        size="sm"
        variant="ghost"
        :end-icon="SquareArrowRightUpIcon"
        :label="t('content.post.openDesign')"
        @click="emit('openDesign', media.openDesignId)"
      />
    </template>

    <PostArtwork v-if="item.design" :design="item.design" />

    <DesignRunStatus v-if="media.run" :run="media.run" />

    <template v-if="media.empty">
      <div class="flex flex-col gap-2">
        <p class="text-body-sm text-text-secondary">{{ t('content.post.noMediaAttached') }}</p>
        <p v-if="media.canAttach" class="text-caption text-text-tertiary">
          {{ t('content.post.designHint') }}
        </p>
      </div>
      <AppButton
        size="sm"
        v-if="media.canAttach"
        variant="ghost"
        class="self-start"
        flush="start"
        :label="t('content.post.attachDesign')"
        @click="attachDialogOpen = true"
      />
    </template>
  </AppPageSection>

  <DesignPicker v-if="media.canAttach" v-model:open="attachDialogOpen" :post-id="item.id" />
</template>
